import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { shiftTruckingDates } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

export default function TruckingDateShift() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(1);

  const mut = useMutation({
    mutationFn: (d) => shiftTruckingDates(d),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["trucking"] });
      toast.success(`Shifted ${res.updated} entr${res.updated === 1 ? "y" : "ies"} by ${res.days > 0 ? "+" : ""}${res.days} day${Math.abs(res.days) === 1 ? "" : "s"}`);
      setOpen(false);
    },
    onError: () => toast.error("Could not shift dates"),
  });

  const apply = () => {
    const n = parseInt(days, 10);
    if (!n) { toast.error("Enter a number of days (e.g. 1 or -2)"); return; }
    mut.mutate(n);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="shift-dates-btn" variant="outline"
          className="rounded-none border-2 border-gray-900 font-bold uppercase tracking-wide">
          <CalendarClock className="w-4 h-4 mr-2" /> Shift Dates
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-2 border-gray-900">
        <DialogHeader>
          <DialogTitle className="font-head uppercase">Shift All Trucking Dates</DialogTitle>
          <DialogDescription>
            Moves every entry's scheduled pickup/delivery date by the number of days below (weather/emergency reschedule).
            Use a negative number to move earlier. Colors update automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <div className="flex flex-wrap gap-2 mb-4">
            {[-2, -1, 1, 2].map((d) => (
              <Button key={d} type="button" variant="outline" data-testid={`shift-preset-${d}`}
                onClick={() => setDays(d)}
                className={`rounded-none border-2 ${Number(days) === d ? "border-gray-900 bg-gray-900 text-white" : "border-gray-300"}`}>
                {d > 0 ? `+${d}` : d} day{Math.abs(d) === 1 ? "" : "s"}
              </Button>
            ))}
          </div>
          <Label className="text-xs uppercase tracking-widest font-bold text-gray-500">Days to shift</Label>
          <Input data-testid="shift-days-input" type="number" value={days}
            onChange={(e) => setDays(e.target.value)}
            className="mt-1 rounded-none border-2 w-32 font-mono-data" />
          <p className="text-sm text-gray-500 mt-2">Positive = later, negative = earlier.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="shift-apply-btn" onClick={apply} disabled={mut.isPending}
            className="rounded-none bg-gray-900 hover:bg-gray-700 text-white font-bold uppercase">
            {mut.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Apply to all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
